import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { useUpdatePhoneNumberMutation } from "@/store/rest_api";
import PhoneNumber from "@/model/interface/PhoneNumber";
import { toast } from "sonner";

interface PhoneNumberAliasCellProps {
  phoneNumber: PhoneNumber;
}

export function PhoneNumberAliasCell({ phoneNumber }: PhoneNumberAliasCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [alias, setAlias] = useState(phoneNumber.alias || "");
  const [updatePhoneNumber, { isLoading }] = useUpdatePhoneNumberMutation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (alias.trim() === phoneNumber.alias) {
      setIsEditing(false);
      return;
    }

    try {
      await updatePhoneNumber({
        id: phoneNumber.id,
        alias: alias.trim(),
      }).unwrap();

      toast.success("Alias updated successfully");
      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to update alias");
      setAlias(phoneNumber.alias || "");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setAlias(phoneNumber.alias || "");
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={alias}
        onChange={(e) => setAlias(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        className="h-8"
      />
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:bg-muted px-2 py-1 rounded min-h-[32px] flex items-center"
      title="Click to edit"
    >
      {phoneNumber.alias || <span className="text-muted-foreground italic">No alias</span>}
    </div>
  );
}
